import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AnalysisSummaryPanel = ({ 
  analysisData,
  onShareAnalysis = () => {},
  onDownloadReport = () => {},
  onViewAllRecommendations = () => {},
  className = ""
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const mockAnalysisData = {
    bikeModel: "Trek Domane AL 2",
    analysisDate: "2025-01-05",
    totalComponents: 12,
    upgradeableComponents: 6,
    goodComponents: 6,
    totalUpgradeCost: 3487,
    estimatedPerformanceGain: 23,
    compatibilityScore: 92,
    recommendations: [
      { category: "Wheels", priority: "high", cost: 1299 },
      { category: "Drivetrain", priority: "high", cost: 899 },
      { category: "Saddle", priority: "medium", cost: 189 },
      { category: "Pedals", priority: "low", cost: 149 },
      { category: "Tires", priority: "medium", cost: 89 },
      { category: "Bar Tape", priority: "low", cost: 35 }
    ],
    performanceMetrics: {
      weight: { current: "22.5 lbs", potential: "19.8 lbs", improvement: "12%" },
      aerodynamics: { current: "Standard", potential: "Enhanced", improvement: "15%" },
      comfort: { current: "Good", potential: "Excellent", improvement: "25%" },
      durability: { current: "Good", potential: "Excellent", improvement: "20%" }
    }
  };

  const data = analysisData || mockAnalysisData;

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 70) return 'text-warning';
    return 'text-error';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-success/10';
    if (score >= 70) return 'bg-warning/10';
    return 'bg-error/10';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground">Analysis Summary</h3>
            <p className="text-sm text-muted-foreground">
              {data?.bikeModel} • Analyzed on {formatDate(data?.analysisDate)}
            </p>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="lg:hidden p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
            aria-label="Toggle summary details"
          >
            <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} />
          </button>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{data?.totalComponents}</div>
            <div className="text-xs text-muted-foreground">Total Components</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">{data?.upgradeableComponents}</div>
            <div className="text-xs text-muted-foreground">Upgradeable</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent">${data?.totalUpgradeCost?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">Total Cost</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${getScoreColor(data?.compatibilityScore)}`}>
              {data?.compatibilityScore}%
            </div>
            <div className="text-xs text-muted-foreground">Compatibility</div>
          </div>
        </div>

        {/* Performance Improvement */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Expected Performance Gain</span>
            <span className="text-sm font-bold text-success">+{data?.estimatedPerformanceGain}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-success h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.min(data?.estimatedPerformanceGain, 100)}%` }}
            />
          </div>
        </div>

        {/* Expandable Details */}
        <div className={`space-y-4 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
          {/* Performance Metrics */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Performance Metrics</h4>
            <div className="space-y-2">
              {Object.entries(data?.performanceMetrics)?.map(([key, metric]) => (
                <div key={key} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={key === 'weight' ? 'Scale' : 
                            key === 'aerodynamics' ? 'Wind' : 
                            key === 'comfort' ? 'Heart' : 'Shield'} 
                      size={16} 
                      color="var(--color-muted-foreground)"
                    />
                    <span className="text-sm text-foreground capitalize">{key}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">
                      {metric?.current} → {metric?.potential}
                    </div>
                    <div className="text-xs font-medium text-success">
                      +{metric?.improvement}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Priority Breakdown */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Upgrade Priority</h4>
            <div className="space-y-2">
              {['high', 'medium', 'low']?.map(priority => {
                const count = data?.recommendations?.filter(r => r?.priority === priority)?.length;
                const totalCost = data?.recommendations?.filter(r => r?.priority === priority)?.reduce((sum, r) => sum + r?.cost, 0);
                
                if (count === 0) return null;
                
                return (
                  <div key={priority} className="flex items-center justify-between p-2 bg-muted/30 rounded-md">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${
                        priority === 'high' ? 'bg-error' : 
                        priority === 'medium' ? 'bg-warning' : 'bg-success'
                      }`} />
                      <span className="text-sm text-foreground capitalize">{priority} Priority</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-foreground">{count} items</div>
                      <div className="text-xs text-muted-foreground">${totalCost?.toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 mt-4 pt-4 border-t border-border">
          <Button
            variant="default"
            size="sm"
            onClick={onViewAllRecommendations}
            iconName="ArrowRight"
            iconPosition="right"
            className="flex-1"
          >
            View All Recommendations
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onShareAnalysis}
            iconName="Share2"
            iconPosition="left"
          >
            Share
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDownloadReport}
            iconName="Download"
            iconPosition="left"
          >
            Download
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisSummaryPanel;